import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  IconPicker,
  Input,
  toastManager,
  type Color,
  type IconName,
} from "@salestrak/ui";

import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateWorkflowDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (workflowId: string) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toKey(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 6)
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateWorkflowDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateWorkflowDialogProps) {
  const { createWorkflow, validateWorkflowKey } = useWorkflowMutations();

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [autoKey, setAutoKey] = useState(true);
  const [iconName, setIconName] = useState<IconName>(ENTITY_KIND_ICON.workflow);
  const [iconColor, setIconColor] = useState<Color>("blue");

  const [nameError, setNameError] = useState("");
  const [keyError, setKeyError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Key validation debounce
  const keyValidateRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setName("");
      setKey("");
      setAutoKey(true);
      setIconName(ENTITY_KIND_ICON.workflow);
      setIconColor("blue");
      setNameError("");
      setKeyError("");
      setSubmitting(false);
    }
  }, [open]);

  // Auto-populate key from name
  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      setNameError("");
      if (autoKey) {
        setKey(toKey(value));
        setKeyError("");
      }
    },
    [autoKey],
  );

  const handleKeyChange = useCallback((value: string) => {
    setAutoKey(false);
    const upper = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase();
    setKey(upper);
    setKeyError("");
  }, []);

  // Debounced key uniqueness validation
  useEffect(() => {
    if (!key || key.length < 3) {
      setKeyError("");
      return;
    }

    clearTimeout(keyValidateRef.current);
    keyValidateRef.current = setTimeout(async () => {
      try {
        const available = await validateWorkflowKey(key);
        if (!available) {
          setKeyError("Key already in use");
        } else {
          setKeyError("");
        }
      } catch {
        // Ignore validation errors
      }
    }, 250);

    return () => clearTimeout(keyValidateRef.current);
  }, [key, validateWorkflowKey]);

  const canSubmit =
    name.trim().length >= 3 &&
    name.trim().length <= 64 &&
    (!key || (key.length >= 3 && key.length <= 6)) &&
    !keyError &&
    !submitting;

  const handleSubmit = useCallback(async () => {
    // Validate name
    if (name.trim().length < 3) {
      setNameError("Name must be at least 3 characters");
      return;
    }
    if (name.trim().length > 64) {
      setNameError("Name must be at most 64 characters");
      return;
    }

    setSubmitting(true);
    try {
      const result = await createWorkflow({
        name: name.trim(),
        key: key || toKey(name.trim()),
        icon: { icon: iconName, color: iconColor },
      });

      if (!result) {
        toastManager.add({ title: "Failed to create workflow", type: "error" });
        return;
      }

      if ("__typename" in result) {
        if (result.__typename === "ValidationError") {
          const msg = "message" in result ? (result as any).message : "Validation error";
          toastManager.add({ title: msg, type: "error" });
          return;
        }
        if (result.__typename === "PermissionDenied") {
          toastManager.add({
            title: "You don't have permission to create workflows",
            type: "error",
          });
          return;
        }
      }

      if ("workflowId" in result) {
        toastManager.add({ title: "Workflow created", type: "success" });
        onOpenChange(false);
        onCreated?.((result as any).workflowId as string);
      }
    } catch (err) {
      toastMutationError(err, "Failed to create workflow");
    } finally {
      setSubmitting(false);
    }
  }, [name, key, iconName, iconColor, createWorkflow, onOpenChange, onCreated]);

  // One surface at every breakpoint: the Dialog sticks to the bottom of the
  // viewport on small screens.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Create workflow</DialogTitle>
        </DialogHeader>
        {/* DialogPanel owns the body gutters and the header/footer spacing
            rules — a bare padded Box leaves no gap above the footer. */}
        <DialogPanel>
        <Box
          as="form"
          gap="lg"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void handleSubmit();
          }}
        >
          <Field invalid={!!nameError}>
            <FieldLabel>Name</FieldLabel>
            <Box direction="row" gap="sm" align="center">
              <IconPicker
                icon={iconName}
                color={iconColor}
                onIconChange={setIconName}
                onColorChange={setIconColor}
                size="sm"
              />
              <Box fill="flex" shrink>
                <Input
                  placeholder="e.g. Sales Pipeline"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  autoFocus
                />
              </Box>
            </Box>
            {nameError ? <FieldError match>{nameError}</FieldError> : null}
          </Field>

          <Field invalid={!!keyError}>
            <FieldLabel>Key</FieldLabel>
            <Input
              placeholder="e.g. SALES"
              value={key}
              onChange={(e) => handleKeyChange(e.target.value)}
              maxLength={6}
            />
            {keyError ? (
              <FieldError match>{keyError}</FieldError>
            ) : (
              <FieldDescription>
                3-6 uppercase characters. Auto-generated from name.
              </FieldDescription>
            )}
          </Field>

          {/* The footer is outside the form element (DialogFooter owns the
              popup's bottom edge), so the submit button targets it by id. */}
        </Box>
        </DialogPanel>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
